import { FC, useState, useCallback } from "react";
import Dialog from "@mui/material/Dialog";
import DialogTitle from "@mui/material/DialogTitle";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import Button from "@mui/material/Button";
import IconButton from "@mui/material/IconButton";
import Typography from "@mui/material/Typography";
import Box from "@mui/material/Box";
import Stack from "@mui/material/Stack";
import Alert from "@mui/material/Alert";
import CircularProgress from "@mui/material/CircularProgress";
import Collapse from "@mui/material/Collapse";
import CloseIcon from "@mui/icons-material/Close";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";
import ExpandLessIcon from "@mui/icons-material/ExpandLess";
import { CsvUploadDropzone } from "../../../components/CsvUploadDropzone";
import {
  useBulkTopicOperations,
  type TopicValidationResult,
} from "../hooks/useBulkTopicOperations";
import { MAX_INTENT_TOPICS, type CachedTopic } from "../slice";

/** Maximum number of invalid IDs to display in the list */
const MAX_DISPLAYED_INVALID_IDS = 10;

interface TopicUploadDialogProps {
  open: boolean;
  onClose: () => void;
  onSuccess: (topics: CachedTopic[]) => void;
}

type DialogState =
  | { status: "idle" }
  | { status: "processing" }
  | { status: "error"; message: string }
  | { status: "success"; result: TopicValidationResult };

export const TopicUploadDialog: FC<TopicUploadDialogProps> = ({
  open,
  onClose,
  onSuccess,
}) => {
  const [dialogState, setDialogState] = useState<DialogState>({
    status: "idle",
  });
  const [showInvalidIds, setShowInvalidIds] = useState(false);
  const { handleFileUpload } = useBulkTopicOperations();

  const handleFilesAccepted = useCallback(
    async (files: File[]) => {
      if (files.length === 0) return;

      setDialogState({ status: "processing" });

      const result = await handleFileUpload(files[0]);

      if (result.error) {
        setDialogState({ status: "error", message: result.error });
        return;
      }

      if (result.validTopics.length === 0) {
        setDialogState({
          status: "error",
          message: "No valid topics found. All topic IDs were invalid.",
        });
        return;
      }

      setDialogState({ status: "success", result });
    },
    [handleFileUpload]
  );

  const handleReset = useCallback(() => {
    setDialogState({ status: "idle" });
    setShowInvalidIds(false);
  }, []);

  const handleDialogClose = useCallback(() => {
    handleReset();
    onClose();
  }, [handleReset, onClose]);

  const handleImport = useCallback(() => {
    if (dialogState.status !== "success") return;
    onSuccess(dialogState.result.validTopics);
    handleDialogClose();
  }, [dialogState, onSuccess, handleDialogClose]);

  const renderIdleContent = () => (
    <Box>
      <Alert severity="info" sx={{ mb: 3 }}>
        <Typography variant="subtitle2" fontWeight="bold" sx={{ mb: 1 }}>
          CSV Format Requirements
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Your CSV file must include a <strong>topic_id</strong> column.
          <br />
          Optionally include a <strong>topic_name</strong> column.
          <br />
          Maximum {MAX_INTENT_TOPICS.toLocaleString()} topics per upload.
        </Typography>
      </Alert>

      <CsvUploadDropzone
        id="topic-csv-upload"
        maxFiles={1}
        onFilesAccepted={handleFilesAccepted}
        icon={<UploadFileIcon sx={{ fontSize: 48 }} color="secondary" />}
        title={
          <Stack spacing={0.5} alignItems="center">
            <Typography
              variant="body2"
              color="textSecondary"
              textAlign="center"
            >
              Drag and drop your CSV file here
              <br />
              or click to browse
            </Typography>
            <Button variant="outlined">Select File</Button>
          </Stack>
        }
        helperText={null}
      />
    </Box>
  );

  const renderProcessingContent = () => (
    <Stack alignItems="center" justifyContent="center" sx={{ py: 6 }}>
      <CircularProgress size={48} sx={{ mb: 2 }} />
      <Typography variant="body1">Processing file...</Typography>
      <Typography variant="body2" color="text.secondary">
        Validating topics against catalog
      </Typography>
    </Stack>
  );

  const renderErrorContent = () => {
    if (dialogState.status !== "error") return null;
    return (
      <Box>
        <Alert severity="error" sx={{ mb: 2 }}>
          <Typography variant="subtitle2" fontWeight="bold">
            Upload Failed
          </Typography>
          <Typography variant="body2">{dialogState.message}</Typography>
        </Alert>
      </Box>
    );
  };

  const renderSuccessContent = () => {
    if (dialogState.status !== "success") return null;

    const { validTopics, invalidTopicIds } = dialogState.result;
    const hasInvalid = invalidTopicIds.length > 0;
    const displayedInvalidIds = invalidTopicIds.slice(
      0,
      MAX_DISPLAYED_INVALID_IDS
    );
    const remainingCount = invalidTopicIds.length - MAX_DISPLAYED_INVALID_IDS;

    return (
      <Box>
        <Alert severity="success" sx={{ mb: 2 }}>
          <Typography variant="body2">
            <strong>{validTopics.length}</strong> valid topic
            {validTopics.length !== 1 ? "s" : ""} found
            {hasInvalid && (
              <>
                , <strong>{invalidTopicIds.length}</strong> invalid
              </>
            )}
          </Typography>
        </Alert>

        {hasInvalid && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              sx={{ width: "100%" }}
            >
              <Typography variant="body2">
                {invalidTopicIds.length} topic ID
                {invalidTopicIds.length !== 1 ? "s" : ""} could not be validated
              </Typography>
              <IconButton
                size="small"
                onClick={() => setShowInvalidIds(!showInvalidIds)}
              >
                {showInvalidIds ? <ExpandLessIcon /> : <ExpandMoreIcon />}
              </IconButton>
            </Stack>
            <Collapse in={showInvalidIds}>
              <Box
                sx={{
                  mt: 1,
                  p: 1,
                  bgcolor: "background.paper",
                  borderRadius: 1,
                  maxHeight: 150,
                  overflow: "auto",
                }}
              >
                {displayedInvalidIds.map((id) => (
                  <Typography
                    key={id}
                    variant="caption"
                    display="block"
                    sx={{ fontFamily: "monospace" }}
                  >
                    {id}
                  </Typography>
                ))}
                {remainingCount > 0 && (
                  <Typography variant="caption" color="text.secondary">
                    ...and {remainingCount} more
                  </Typography>
                )}
              </Box>
            </Collapse>
          </Alert>
        )}

        <Typography variant="body2" color="text.secondary">
          Click Import to add the valid topics to your selection.
        </Typography>
      </Box>
    );
  };

  const renderContent = () => {
    switch (dialogState.status) {
      case "processing":
        return renderProcessingContent();
      case "error":
        return renderErrorContent();
      case "success":
        return renderSuccessContent();
      default:
        return renderIdleContent();
    }
  };

  const renderActions = () => {
    switch (dialogState.status) {
      case "processing":
        return (
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
        );
      case "error":
        return (
          <>
            <Button onClick={handleDialogClose} color="secondary">
              Cancel
            </Button>
            <Button onClick={handleReset} variant="contained" color="primary">
              Try Again
            </Button>
          </>
        );
      case "success":
        return (
          <>
            <Button onClick={handleReset} color="secondary">
              Upload Another
            </Button>
            <Button onClick={handleImport} variant="contained" color="primary">
              Import
            </Button>
          </>
        );
      default:
        return (
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
        );
    }
  };

  return (
    <Dialog
      open={open}
      onClose={handleDialogClose}
      maxWidth="sm"
      fullWidth
      slotProps={{ paper: { sx: { minHeight: 350 } } }}
    >
      <DialogTitle sx={{ m: 0, p: 2 }}>
        <Typography variant="h6" component="div" fontWeight={600}>
          Import Intent Topics from CSV
        </Typography>
        <IconButton
          aria-label="close"
          onClick={handleDialogClose}
          sx={{
            position: "absolute",
            right: 8,
            top: 8,
            color: (theme) => theme.palette.grey[500],
          }}
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>

      <DialogContent dividers>{renderContent()}</DialogContent>

      <DialogActions sx={{ p: 2 }}>{renderActions()}</DialogActions>
    </Dialog>
  );
};
